// =============================================================================
// Terminal Type Indicator - 显示终端类型和持久化信息
// =============================================================================

import React, { useState, useEffect } from 'react';
import { 
  Server, 
  Bot, 
  Code, 
  Clock, 
  Eye, 
  BarChart3, 
  Terminal,
  Settings,
  Info
} from 'lucide-react';

const TERMINAL_TYPE_ICONS = {
  service: Server,
  ai_tool: Bot,
  development: Code,
  temporary: Clock,
  monitoring: BarChart3,
  general: Terminal
};

const TERMINAL_TYPE_COLORS = {
  service: 'text-green-600 bg-green-100',
  ai_tool: 'text-purple-600 bg-purple-100',
  development: 'text-blue-600 bg-blue-100',
  temporary: 'text-gray-600 bg-gray-100',
  monitoring: 'text-orange-600 bg-orange-100',
  general: 'text-slate-600 bg-slate-100'
};

const PERSISTENCE_COLORS = {
  high: 'text-red-600',
  standard: 'text-blue-600',
  low: 'text-yellow-600',
  minimal: 'text-gray-600'
};

export default function TerminalTypeIndicator({
  terminalId,
  terminalType = 'general',
  persistenceLevel = 'standard',
  tags = [],
  onTypeChange,
  showDetails = false,
  compact = false
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [isChangingType, setIsChangingType] = useState(false);

  useEffect(() => {
    // 获取可用的终端类型
    fetchAvailableTypes();
  }, []);

  const fetchAvailableTypes = async () => {
    try {
      const response = await fetch('/api/terminals/types/info');
      const data = await response.json();
      if (data.success) {
        setAvailableTypes(data.data.types);
      }
    } catch (error) {
      console.error('Failed to fetch terminal types:', error);
    }
  };

  const handleTypeChange = async (newType) => {
    if (!onTypeChange) return;
    
    setIsChangingType(true);
    try {
      await onTypeChange(terminalId, newType);
    } catch (error) {
      console.error('Failed to change terminal type:', error);
    } finally {
      setIsChangingType(false);
    }
  };

  const IconComponent = TERMINAL_TYPE_ICONS[terminalType] || Terminal;
  const typeColor = TERMINAL_TYPE_COLORS[terminalType] || TERMINAL_TYPE_COLORS.general;
  const persistenceColor = PERSISTENCE_COLORS[persistenceLevel] || PERSISTENCE_COLORS.standard;

  const currentTypeInfo = availableTypes.find(t => t.id === terminalType);

  // 紧凑模式只显示图标
  if (compact) {
    return (
      <div
        className={`flex items-center space-x-1 px-1 py-0.5 rounded text-xs font-medium ${typeColor}`}
        title={`${currentTypeInfo?.name || terminalType} - ${currentTypeInfo?.description || ''}`}
      >
        <IconComponent size={10} />
        <span className="text-xs font-mono">{persistenceLevel.charAt(0).toUpperCase()}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      {/* 终端类型指示器 */}
      <div
        className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium cursor-pointer transition-all ${typeColor} ${
          isExpanded ? 'ring-2 ring-blue-300' : 'hover:ring-1 ring-gray-300'
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
        title={currentTypeInfo?.description || '点击查看详情'}
      >
        <IconComponent size={12} />
        <span>{currentTypeInfo?.name || terminalType}</span>
        {showDetails && (
          <Info size={10} className="opacity-60" />
        )}
      </div>

      {/* 持久化级别指示器 */}
      <div className={`text-xs font-mono ${persistenceColor}`} title={`持久化级别: ${persistenceLevel}`}>
        {persistenceLevel.charAt(0).toUpperCase()}
      </div>

      {/* 标签 */}
      {tags.length > 0 && (
        <div className="flex space-x-1">
          {tags.slice(0, 2).map(tag => (
            <span
              key={tag}
              className="px-1 py-0.5 text-xs bg-gray-200 text-gray-600 rounded"
            >
              {tag}
            </span>
          ))}
          {tags.length > 2 && (
            <span className="text-xs text-gray-400">+{tags.length - 2}</span>
          )}
        </div>
      )}

      {/* 详细信息面板 */}
      {isExpanded && (
        <div className="absolute top-full left-0 mt-2 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-64">
          <div className="space-y-3">
            {/* 当前类型信息 */}
            <div>
              <h4 className="font-medium text-sm text-gray-900 mb-1">当前类型</h4>
              <div className="flex items-center space-x-2">
                <IconComponent size={16} className={typeColor.split(' ')[0]} />
                <div>
                  <div className="font-medium text-sm">{currentTypeInfo?.name}</div>
                  <div className="text-xs text-gray-500">{currentTypeInfo?.description}</div>
                </div>
              </div>
            </div>

            {/* 持久化信息 */}
            <div>
              <h4 className="font-medium text-sm text-gray-900 mb-1">持久化配置</h4>
              <div className="text-xs space-y-1">
                <div>级别: <span className={persistenceColor}>{persistenceLevel}</span></div>
                {currentTypeInfo && (
                  <>
                    <div>缓冲区: {(currentTypeInfo.maxBufferSize / 1024).toFixed(0)}KB</div>
                    <div>历史行数: {currentTypeInfo.maxScrollbackLines}</div>
                    <div>自动清理: {Math.floor(currentTypeInfo.autoCleanupAfter / 3600)}小时</div>
                  </>
                )}
              </div>
            </div>

            {/* 标签 */}
            {tags.length > 0 && (
              <div>
                <h4 className="font-medium text-sm text-gray-900 mb-1">标签</h4>
                <div className="flex flex-wrap gap-1">
                  {tags.map(tag => (
                    <span 
                      key={tag}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 类型切换 */}
            {onTypeChange && availableTypes.length > 0 && (
              <div>
                <h4 className="font-medium text-sm text-gray-900 mb-1">切换类型</h4>
                <div className="grid grid-cols-2 gap-1">
                  {availableTypes.map(type => {
                    const TypeIcon = TERMINAL_TYPE_ICONS[type.id] || Terminal;
                    const isActive = type.id === terminalType;
                    
                    return (
                      <button
                        key={type.id}
                        onClick={() => handleTypeChange(type.id)}
                        disabled={isActive || isChangingType}
                        className={`flex items-center space-x-1 px-2 py-1 text-xs rounded transition-colors ${
                          isActive 
                            ? 'bg-blue-100 text-blue-700 cursor-default' 
                            : 'hover:bg-gray-100 text-gray-700'
                        } ${isChangingType ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={type.description}
                      >
                        <TypeIcon size={12} />
                        <span>{type.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* 关闭按钮 */}
          <button
            onClick={() => setIsExpanded(false)}
            className="absolute top-1 right-1 p-1 text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

// 简化版本 - 只显示图标
export function TerminalTypeIcon({ terminalType = 'general', size = 16 }) {
  const IconComponent = TERMINAL_TYPE_ICONS[terminalType] || Terminal;
  const typeColor = TERMINAL_TYPE_COLORS[terminalType] || TERMINAL_TYPE_COLORS.general;
  
  return (
    <div className={`p-1 rounded ${typeColor}`}>
      <IconComponent size={size} />
    </div>
  );
}

// Hook for terminal type management
export function useTerminalType(terminalId) {
  const [terminalInfo, setTerminalInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const changeTerminalType = async (newType) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/terminals/${terminalId}/type`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ terminalType: newType }),
      });

      const data = await response.json();
      if (data.success) {
        setTerminalInfo(prev => ({
          ...prev,
          terminalType: newType,
          persistenceLevel: data.data.persistenceLevel,
          tags: data.data.tags
        }));
        return true;
      } else {
        throw new Error(data.error || 'Failed to change terminal type');
      }
    } catch (error) {
      console.error('Error changing terminal type:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    terminalInfo,
    changeTerminalType,
    loading
  };
}
